import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScrollSentinel from '../InfiniteScrollSentinel';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, LogIn, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import postService, { DartPost } from '../../services/postService';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import DynamicSlider from '../slider/DynamicSlider';
import { t } from '../../i18n';

const ACCENT = '#3fced1';
const PAGE_SIZE = 10;

export type PostFeedMode = 'feed' | 'forYou';
/** Slider-Flächen zwischen 1./2. und 4./5. Beitrag (Marcel 2026-09-29) – eigene Location je Tab und Stelle, Katalog: slider-locations.js */
const FEED_SLIDER_LOCATION = 'feed_after_first';
const FOR_YOU_SLIDER_LOCATION = 'for_you_after_first';
const FEED_FOURTH_SLIDER_LOCATION = 'feed_after_fourth';
const FOR_YOU_FOURTH_SLIDER_LOCATION = 'for_you_after_fourth';
export const feedSliderLocation = (mode: PostFeedMode): string => (mode === 'forYou' ? FOR_YOU_SLIDER_LOCATION : FEED_SLIDER_LOCATION);
/** Location für die Fläche nach dem Beitrag mit Index `idx` (0 = nach dem 1., 3 = nach dem 4.), sonst null (PURE). */
export const feedSliderLocationAt = (mode: PostFeedMode, idx: number): string | null => {
  if (idx === 0) return feedSliderLocation(mode);
  if (idx === 3) return mode === 'forYou' ? FOR_YOU_FOURTH_SLIDER_LOCATION : FEED_FOURTH_SLIDER_LOCATION;
  return null;
};

interface PostFeedListProps {
  /** 'feed' = alle neuesten Beiträge, 'forYou' = gefolgte Autoren (auth). */
  mode: PostFeedMode;
}

/**
 * Wiederverwendbare Feed-Liste (Startseiten-Tabs + /feed): lädt den
 * öffentlichen Feed bzw. "Für dich" (ohne Login → Anmelde-Hinweis statt
 * API-Call), zeigt PostCards max. 600px zentriert mit "Mehr laden"-Pagination
 * und einen Posten-Button, der den PostComposer öffnet (Gäste → Login-Redirect
 * mit Rücksprung, Muster ListingFollowButton).
 */
const PostFeedList: React.FC<PostFeedListProps> = ({ mode }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [posts, setPosts] = useState<DartPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const fetchPage = useCallback(
    async (offset: number) => {
      const params = { limit: PAGE_SIZE, offset };
      return mode === 'forYou' ? postService.getForYou(params) : postService.getFeed(params);
    },
    [mode]
  );

  useEffect(() => {
    let cancelled = false;
    if (mode === 'forYou' && !isAuthenticated) {
      setPosts([]);
      setLoading(false);
      setHasMore(false);
      return;
    }
    setLoading(true);
    fetchPage(0).then((result) => {
      if (cancelled) return;
      setPosts(result.posts);
      setHasMore(result.posts.length >= PAGE_SIZE);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, isAuthenticated, fetchPage]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const result = await fetchPage(posts.length);
    setPosts((prev) => {
      const known = new Set(prev.map((p) => p.id));
      return [...prev, ...result.posts.filter((p) => !known.has(p.id))];
    });
    setHasMore(result.posts.length >= PAGE_SIZE);
    setLoadingMore(false);
  };

  const handleRemoved = (postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const handleCreated = (post: DartPost) => {
    setPosts((prev) => [post, ...prev]);
  };

  // Gäste dürfen den Composer nicht nutzen → Login mit Rücksprung
  const openComposer = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }
    setComposerOpen(true);
  };

  // ?neu=1 (z.B. aus dem "Neuer Eintrag"-Modal) öffnet den Composer direkt —
  // Param danach aus der URL entfernen, damit Reload ihn nicht erneut öffnet
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('neu') === '1') {
      navigate(location.pathname, { replace: true });
      openComposer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loginHint = mode === 'forYou' && !isAuthenticated;

  return (
    <div className="w-full max-w-[600px] mx-auto">
      {/* Posten-Button (Gäste → Login-Redirect) */}
      <div className="flex justify-end mb-5">
        <button
          type="button"
          onClick={openComposer}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-black transition hover:opacity-90 shrink-0"
          style={{ backgroundColor: ACCENT }}
        >
          <Plus size={16} />
          {t('web.component.post-composer.posten-2')}
        </button>
      </div>

      {loginHint ? (
        <div className="text-center py-16">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <LogIn size={20} style={{ color: ACCENT }} />
          </div>
          <p className="text-zinc-400 mb-4">{t('web.component.post-feed-list.melde-dich-an-um-deinen')}</p>
          <Link
            to="/login"
            className="inline-block px-5 py-2.5 rounded-xl text-sm font-semibold text-black transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {t('web.component.header.anmelden')}
          </Link>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-zinc-500" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <ImageIcon size={20} style={{ color: ACCENT }} />
          </div>
          <p className="text-zinc-400">
            {mode === 'forYou'
              ? t('web.component.post-feed-list.noch-keine-beitraege-folge-spielern')
              : t('web.component.post-feed-list.noch-keine-beitraege-sei-der')}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-10">
            {posts.map((post, idx) => (
              <React.Fragment key={post.id}>
                <PostCard post={post} onRemoved={handleRemoved} />
                {feedSliderLocationAt(mode, idx) && <DynamicSlider location={feedSliderLocationAt(mode, idx) as string} className="w-full" />}
              </React.Fragment>
            ))}
          </div>

          {/* Automatisches Nachladen statt „Mehr laden“ (Marcel 2026-09-24) */}
          <InfiniteScrollSentinel hasMore={hasMore} loading={loadingMore} onLoadMore={loadMore} />
        </>
      )}

      <PostComposer
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default PostFeedList;
